import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/auth-context';
import { ToastProvider } from './contexts/toast-context';
import { LanguageProvider } from './contexts/language-context';
import Layout from './components/Layout';
import { MovieList, TVList } from './components/media-list-container';
import MovieDetail from './components/movie-detail';
import TVDetail from './components/tv-detail';
import SearchResults from './components/search-results';
import { Favorites, Watchlist, Watched } from './components/user-lists';
import CollectionsPage from './components/collections-page';
import CollectionDetail from './components/collection-detail';
import CinemaShowtimes from './components/cinema-showtimes';
import ContactPage from './components/contact-page';
import MediaFilters from './components/media-filters';
import useUnifiedFilters from './hooks/use-unified-filters';
import useViewMode from './hooks/use-view-mode';
import useReloadReset from './hooks/use-reload-reset';
import { MOVIE_GENRES, TV_GENRES, MOVIE_SORT_OPTIONS, TV_SORT_OPTIONS, QUERY_CONFIG, MOVIE_HERO_VARIANTS, TV_HERO_VARIANTS } from './constants';
import { GOOGLE_CLIENT_ID } from './config/app-config';
import TagManager from 'react-gtm-module';
import { trackPageView } from './utils/gtm';
import MetaTags from './components/meta-tags';
import { HomeHeroSection } from './components/home-hero-section';

const randomItem = (arr) => arr[Math.floor(Math.random() * arr.length)];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: QUERY_CONFIG,
  },
});

function App() {
  useEffect(() => {
    TagManager.initialize({
      gtmId: 'GTM-KFCS888K'
    });
    
    // CRITICAL: Disable browser's automatic scroll restoration to prevent conflicts with manual restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const appContent = (
    <LanguageProvider>
      <AuthProvider>
        <ToastProvider>
          <Provider store={store}>
            <Router>
              <NuqsAdapter>
                <AppRoutes />
              </NuqsAdapter>
            </Router>
          </Provider>
        </ToastProvider>
      </AuthProvider>
    </LanguageProvider>
  );

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        {GOOGLE_CLIENT_ID ? (
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            {appContent}
          </GoogleOAuthProvider>
        ) : (
          appContent
        )}
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname, {
      page_title: document.title,
      page_location: window.location.href,
    });
  }, [location]);

  useEffect(() => {
    const isDetailPage = location.pathname.startsWith('/movie/') || location.pathname.startsWith('/tv/');
    
    if (isDetailPage) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<MoviesPage />} />
      <Route path="/verify-email" element={<MoviesPage />} />
      <Route path="/movie/:id" element={<MovieDetail />} />
      <Route path="/tv" element={<TVPage />} />
      <Route path="/tv/:id" element={<TVDetail />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/cinema" element={<CinemaShowtimes />} />
      <Route path="/kontaktai" element={<ContactPage />} />
      <Route path="/collections" element={<CollectionsPage />} />
      <Route path="/collections/:id" element={<CollectionDetail />} />
      <Route path="/favorites" element={<Favorites />} />
      <Route path="/watchlist" element={<Watchlist />} />
      <Route path="/watched" element={<Watched />} />
    </Routes>
  );
}

function MoviesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useViewMode();
  const [hero] = useState(() => randomItem(MOVIE_HERO_VARIANTS));
  
  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters
  } = useUnifiedFilters({}, 'movie');

  // Reset filters and view mode on full page reload
  useReloadReset(resetFilters, setViewMode);

  return (
    <Layout searchQuery={searchQuery} setSearchQuery={setSearchQuery} resetFilters={resetFilters}>
      <MetaTags
        title="FilmųDB - Filmų ir TV serialų atradimo platforma"
        description="Filmų įvertinimai iš IMDb, Rotten Tomatoes ir kitų šaltinių vienoje vietoje. Sužinok, kur žiūrėti Lietuvoje. 50K+ filmų, 1M+ atsiliepimų."
        url="https://filmudb.lt"
        keywords="filmai, kino duomenų bazė, filmų atsiliepimas, populiarūs filmai, lietuvių filmų duomenų bazė"
      />
      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        <HomeHeroSection hero={hero}>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">50K+</div>
            <div className="text-xs sm:text-sm text-gray-400">Filmų</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">20K+</div>
            <div className="text-xs sm:text-sm text-gray-400">TV Serialų</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">1M+</div>
            <div className="text-xs sm:text-sm text-gray-400">Atsiliepimų</div>
          </div>
        </HomeHeroSection>

      {/* Filters */}
      <MediaFilters
        mediaType="movie"
        title="Filmai"
        filters={filters}
        onFiltersChange={updateFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        genres={MOVIE_GENRES}
        sortOptions={MOVIE_SORT_OPTIONS}
        resetFilters={resetFilters}
        hasActiveFilters={hasActiveFilters}
        resetViewMode={setViewMode}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <MovieList 
          searchQuery={searchQuery} 
          filters={filters}
          viewMode={viewMode}
        />
      </main>
      </div>
    </Layout>
  );
}

function TVPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useViewMode();
  const [hero] = useState(() => randomItem(TV_HERO_VARIANTS));
  
  const {
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilters
  } = useUnifiedFilters({}, 'tv');

  // Reset filters and view mode on full page reload
  useReloadReset(resetFilters, setViewMode);

  return (
    <Layout searchQuery={searchQuery} setSearchQuery={setSearchQuery} resetFilters={resetFilters}>
      <MetaTags
        title="FilmųDB - TV serialų atradimo platforma"
        description="Serialų įvertinimai iš IMDb, Rotten Tomatoes ir kitų šaltinių vienoje vietoje. Sužinok, kur žiūrėti Lietuvoje. 20K+ serialų, 800K+ atsiliepimų."
        url="https://filmudb.lt/tv"
        keywords="TV serialai, televizijos serialai, serialų duomenų bazė, serialų atsiliepimas, populiarūs serialai, lietuvių TV serialų duomenų bazė"
      />
      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        <HomeHeroSection hero={hero}>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">20K+</div>
            <div className="text-xs sm:text-sm text-gray-400">TV Serialų</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">500K+</div>
            <div className="text-xs sm:text-sm text-gray-400">Epizodų</div>
          </div>
          <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl px-4 py-3 sm:px-6 sm:py-4 border border-dark-700 min-w-[100px]">
            <div className="text-xl sm:text-2xl font-bold text-primary-400">800K+</div>
            <div className="text-xs sm:text-sm text-gray-400">Atsiliepimų</div>
          </div>
        </HomeHeroSection>

      {/* Filters */}
      <MediaFilters
        mediaType="tv"
        title="Populiarūs TV serialai"
        subtitle=""
        filters={filters}
        onFiltersChange={updateFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        genres={TV_GENRES}
        sortOptions={TV_SORT_OPTIONS}
        resetFilters={resetFilters}
        hasActiveFilters={hasActiveFilters}
        resetViewMode={setViewMode}
      />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <TVList 
          searchQuery={searchQuery} 
          filters={filters}
          viewMode={viewMode}
        />
      </main>
      </div>
    </Layout>
  );
}

export default App;
